import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "completed") {
          await refreshUser();
          track("trial_start");
          setStatus("ok");
          setTimeout(() => navigate("/oggi", { replace: true }), 1400);
          return;
        }
      } catch {
        /* retry */
      }
      if (attempts.current >= 12) {
        setStatus("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-8 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#27272A] border-t-[#D4AF37]" />
          <p className="mt-5 text-[#B4B4BC]">Confermo il tuo abbonamento…</p>
        </>
      )}
      {status === "ok" && (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 size={64} className="mx-auto text-[#10B981]" />
          <h1 className="font-serif-el mt-5 text-3xl font-semibold text-[#FAF9F6]">Ci siamo!</h1>
          <p className="mt-2 text-[#B4B4BC]">Il tuo percorso è pronto. Ti porto al Giorno 1.</p>
        </motion.div>
      )}
      {status === "error" && (
        <>
          <XCircle size={56} className="mx-auto text-[#F87171]" />
          <h1 className="font-serif-el mt-5 text-3xl font-semibold text-[#FAF9F6]">Pagamento non confermato</h1>
          <p className="mt-2 text-[#B4B4BC]">Se hai completato il pagamento, riprova tra poco.</p>
          <button onClick={() => navigate("/paywall")} data-testid="payment-retry-button" className="btn-gold mt-6 rounded-full px-6 py-3 text-base uppercase tracking-wide">
            Torna al piano
          </button>
        </>
      )}
    </div>
  );
}
